export const compatibilityLevels = ['Unknown', 'Poor', 'Neutral', 'Good', 'Excellent'] as const;
export const pairingRuleLevels = ['none', 'preferred', 'strongly_preferred', 'required_when_both_selected', 'schedule_only_as_pair', 'avoid_pairing', 'excluded'] as const;

export type PartnerCompatibility = (typeof compatibilityLevels)[number];
export type PartnerPairingRule = (typeof pairingRuleLevels)[number];

export const pairingRuleLabels: Record<PartnerPairingRule, string> = {
  none: 'No Special Preference',
  preferred: 'Prefer Together',
  strongly_preferred: 'Strongly Prefer Together',
  required_when_both_selected: 'Must Pair When Both Play',
  schedule_only_as_pair: 'Schedule Only as a Pair',
  avoid_pairing: 'Avoid Pairing',
  excluded: 'Do Not Pair',
};

export const pairingRuleHelpText: Record<PartnerPairingRule, string> = {
  none: 'Normal compatibility and lineup objectives apply.',
  preferred: 'The engine will favor this pairing but may separate them.',
  strongly_preferred: 'The engine will avoid separating them unless necessary.',
  required_when_both_selected: 'When both are in a lineup, they must share a doubles line.',
  schedule_only_as_pair: 'For doubles, neither player will be recommended without the other.',
  avoid_pairing: 'The engine will try to avoid this pairing, but may use it when it is still the best valid lineup.',
  excluded: 'The engine will never place these players on the same doubles line.',
};

export type PartnerPreferenceLike = {
  workspace_id: string;
  team_id?: string | null;
  player1_id: string;
  player2_id: string;
  compatibility?: string | null;
  pairing_rule?: string | null;
  notes?: string | null;
};

export function normalizePartnerPair(playerAId: string, playerBId: string): [string, string] {
  if (!playerAId || !playerBId) throw new Error('Both players are required.');
  if (playerAId === playerBId) throw new Error('Choose two different players.');
  return playerAId < playerBId ? [playerAId, playerBId] : [playerBId, playerAId];
}

export function partnerPreferenceKey(workspaceId: string, playerAId: string, playerBId: string): string {
  const [player1Id, player2Id] = normalizePartnerPair(playerAId, playerBId);
  return `${workspaceId}:${player1Id}:${player2Id}`;
}

export function normalizedPartnerPreferencePayload(workspaceId: string, playerAId: string, playerBId: string) {
  const [player1Id, player2Id] = normalizePartnerPair(playerAId, playerBId);
  return { workspace_id: workspaceId, player1_id: player1Id, player2_id: player2Id };
}

export function normalizeCompatibility(value?: string | null): PartnerCompatibility {
  return compatibilityLevels.includes(value as PartnerCompatibility) ? (value as PartnerCompatibility) : 'Unknown';
}

export function normalizePairingRule(value?: string | null): PartnerPairingRule {
  return pairingRuleLevels.includes(value as PartnerPairingRule) ? (value as PartnerPairingRule) : 'none';
}

export function partnerPreferenceForPair<T extends PartnerPreferenceLike>(
  preferences: T[],
  workspaceId: string,
  playerAId: string,
  playerBId: string,
): T | null {
  const key = partnerPreferenceKey(workspaceId, playerAId, playerBId);
  return preferences.find((preference) => partnerPreferenceKey(preference.workspace_id, preference.player1_id, preference.player2_id) === key) || null;
}

export function compatibilitySortRank(value?: string | null): number {
  return compatibilityLevels.indexOf(normalizeCompatibility(value));
}

export function compatibilityChipColor(value?: string | null): 'success' | 'info' | 'default' | 'warning' | 'error' {
  const compatibility = normalizeCompatibility(value);
  if (compatibility === 'Excellent') return 'success';
  if (compatibility === 'Good') return 'info';
  if (compatibility === 'Poor') return 'warning';
  return 'default';
}

export function pairingRuleChipColor(value?: string | null): 'success' | 'info' | 'default' | 'warning' | 'error' {
  const rule = normalizePairingRule(value);
  if (rule === 'preferred') return 'info';
  if (rule === 'strongly_preferred') return 'success';
  if (rule === 'required_when_both_selected' || rule === 'schedule_only_as_pair') return 'warning';
  if (rule === 'avoid_pairing') return 'warning';
  if (rule === 'excluded') return 'error';
  return 'default';
}

export function isHardPairingRule(value?: string | null): boolean {
  return ['required_when_both_selected', 'schedule_only_as_pair', 'excluded'].includes(normalizePairingRule(value));
}

export function isSoftPairingRule(value?: string | null): boolean {
  return ['preferred', 'strongly_preferred', 'avoid_pairing'].includes(normalizePairingRule(value));
}
